<script lang="ts">
export default {
  name: 'ListingNew',
  inheritAttrs: false,
  customOptions: {}
}
</script>

<script setup lang="ts">
import {computed, onBeforeMount, ref, reactive, watch} from 'vue'
import { watchIgnorable } from '@vueuse/core'
import {useGlobalStore} from "../../stores/global";
import LoginTest from "../../components/LoginTest.vue";
import VueDropzone from '../../components/Dropzone.vue'
import Editor from '../../components/Editor.vue'
import Tip from '../../components/Tip.vue'
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import { TreeNode } from 'primevue/treenode';
import TreeSelect from 'primevue/treeselect';
import Prices from './new/Prices.vue';
import Address from "../../components/Address.vue";
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
  verify_email_url: { type: String, required: true },
  login_url: { type: String, required: true },
  forgot_password_url: { type: String, default: '' },
  image_url: { type: String, required: true },
  save_url: { type: String, required: true },
  registration: { type: Object, required: true },
  employeeOptions: { type: Array<TreeNode>, required: true },
  regionOptions: { type: Array<TreeNode>, required: true },
  sectorOptions: { type: Array<TreeNode>, required: true },
  legalOptions: { type: Array<TreeNode>, required: true },
  transactionOptions: { type: Array<TreeNode>, required: true },
  acquisitionOptions: { type: Array<TreeNode>, required: true },
  phaseOptions: { type: Array<TreeNode>, required: true },
  turnoverOptions: { type: Array<TreeNode>, required: true },
  earningsOptions: { type: Array<TreeNode>, required: true },
  priceOptions: { type: Array<TreeNode>, required: true },
  prices: { type: Object, required: true },
  trans: { type: Object, required: true },
});

const toast = useToast();

let expandedLocations: { [key: string]: boolean } = {};
props.regionOptions.forEach(e => {
  expandedLocations[e.key] = true;
  e.children?.forEach(ee => {
    expandedLocations[ee.key] = true;
  });
});
onBeforeMount(beforeMount);

let my_registration = reactive(props.registration);

const PURPOSE_SELL = 1;
// const PURPOSE_BUY = 2;
const MAX_FILES = 5;

const is_sale = my_registration.purpose === PURPOSE_SELL;
const is_package_light = my_registration.package === 6;

const store = useGlobalStore();
const active_lang = ref(store.locale);

const saving = ref(false);

if (is_sale && (store.webspace === 'btknl')) {
  watch(() => my_registration.businessReferences.legalEntity, (_new, _old) => {
    if (_new === 1 || _new === 2) {
      my_registration.businessReferences.typeOfTransaction = 2;
    }
  });
}

const location_search_params = new URLSearchParams(window.location.search);
function beforeMount() {
  if (window.location.search) {
    const s = parseInt(location_search_params.get('step') || '0');
    if ((s > 0) && (s <= my_registration.businessReferences.step_max)) {
      ignoreUpdates(() => step.value = s);
    }
  }
  if (my_registration.businessReferences.step_max > 1) {
    ignoreUpdates(() => step.value = my_registration.businessReferences.step_max);
  } else if (step.value === 0 && store.user.id) {
    ignoreUpdates(() => step.value = 1);
  }
  if (store.user.id) {
    enableAside();
  }
}

function enableAside() {
  const managerInfo = document.querySelector('#aside .manager-info') as HTMLElement | null;
  const infoBox = document.querySelector('#aside .info-box') as HTMLElement | null;

  if (managerInfo) {
    managerInfo.style.display = 'none';
  }

  if (infoBox) {
    infoBox.style.display = 'block';
  }
}

function isInvalidSelect(key: string, vr: boolean = true) {
  if (vr && !validation_run.value) {
    return false;
  }
  const v = my_registration.businessReferences[key];
  switch (typeof v) {
    case 'object':
      return (Object.keys(v).length < 1);
    case 'string':
      return (v.length < 1);
  }

  return !(v > 0);
}

function msg(detail: string) {
  toast.add({
    severity: 'error',
    summary: props.trans.toast_error_title,
    detail: detail,
    life: 6000,
  });
}

const formTop = ref<HTMLElement | null>(null);
function toTop() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Add a timeout, because the validation class is added after the DOM update
  setTimeout(() => {
    const invalidElements = document.querySelectorAll('.p-invalid, .form :is(input, textarea):not(.valid):user-invalid');
    if (invalidElements.length > 0) {
      msg(props.trans.toast_error_message);

      const firstInvalidElement = invalidElements[0] as HTMLElement;
      firstInvalidElement.scrollIntoView({
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
        block: 'center', // Center the element in the viewport
      });
      if (
          firstInvalidElement instanceof HTMLInputElement ||
          firstInvalidElement instanceof HTMLSelectElement ||
          firstInvalidElement instanceof HTMLTextAreaElement
      ) {
        firstInvalidElement.focus();
      } else {
        const focusableElements = firstInvalidElement.querySelectorAll('input, select, textarea');
        if (focusableElements.length > 0) {
          (focusableElements[0] as HTMLElement).focus();
        }
      }
    } else {
      formTop.value?.scrollIntoView({
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
      });
    }
  }, 0);
}
const step = ref(0);
function prevStep() {
  validation_run.value = false;
  if (!is_sale && (step.value === 4)) {
    step.value -= 2; // skip step 3 for 'buy' because images are not required
  } else {
    step.value--;
  }
  toTop();
}
function nextStep() {
  validation_run.value = false;
  if (is_package_light) {
    if (step.value === 1) {
      step.value = 5
    } else {
      step.value++;
    }
  } else {
    if (!is_sale && (step.value === 2)) {
      step.value += 2; // skip step 3 for 'buy' because images are not required
    } else {
      step.value++;
    }
  }
  toTop();
}

// Initialize personal data
{
  my_registration.personalData.formOfAddress || (my_registration.personalData.formOfAddress = store.user.formOfAddress);
  my_registration.personalData.firstName || (my_registration.personalData.firstName = store.user.firstName);
  my_registration.personalData.lastName || (my_registration.personalData.lastName = store.user.lastName);
  my_registration.personalData.phone || (my_registration.personalData.phone = store.user.phone);
  my_registration.personalData.postcode || (my_registration.personalData.postcode = store.user.address.postcode);
  my_registration.personalData.number || (my_registration.personalData.number = store.user.address.number);
  my_registration.personalData.street || (my_registration.personalData.street = store.user.address.street);
  my_registration.personalData.town || (my_registration.personalData.town = store.user.address.town);
}

function validateStep(step: number): boolean {
  let valid = true;
  switch (step) {
    case 1:
      valid = !(
          isInvalidSelect('employeesInFTE', false) ||
          isInvalidSelect('location', false) ||
          (is_sale && isInvalidSelect('sector', false)) ||
          isInvalidSelect('typeOfCompany', false) ||
          isInvalidSelect('legalEntity', false) ||
          isInvalidSelect('typeOfTransaction', false) ||
          isInvalidSelect('typeOfAcquisition', false) ||
          isInvalidSelect('lifePhaseEnterprise', false) ||
          isInvalidSelect('turnoverLastFinancialYear', false) ||
          isInvalidSelect('earningBeforeTaxes', false) ||
          isInvalidSelect('askingPrice', false)
      );
      break;
    case 2:
      // Only title is required in one language
      valid = (my_registration.description[store.locale].name !== '');
      break;
    case 5:
      valid = (!is_sale || my_registration.personalData.name) &&
          my_registration.personalData.formOfAddress &&
          my_registration.personalData.firstName &&
          my_registration.personalData.lastName &&
          my_registration.personalData.phone &&
          my_registration.personalData.postcode &&
          my_registration.personalData.number &&
          my_registration.personalData.street &&
          my_registration.personalData.town &&
          my_registration.personalData.presetContactTimeId &&
          my_registration.personalData.terms
      ;

      if (!store.user.id) {
        valid = valid &&
            (my_registration.personalData.password.length > 5) &&
            (my_registration.personalData.password === my_registration.personalData.password_repeat);
      }
      break;
  }

  return valid;
}
const {ignoreUpdates} = watchIgnorable(step, (_new, _old) => {
  if (_old === 0) {
    beforeMount();
  }
  if ((_new < _old) && (_old === my_registration.businessReferences.step_max)) {
    return;
  }

  let valid = validateStep(_old);
  if (!valid) {
    validation_run.value = true;
    ignoreUpdates(() => step.value = _old);

    return;
  }

  if (_new > my_registration.businessReferences.step_max) {
    my_registration.businessReferences.step_max = _new;
  }
  save();

  if (_new > 1) {
    location_search_params.set('step', _new.toString());
  } else {
    location_search_params.delete('step');
  }
  const search_params = location_search_params.toString();

  window.history.pushState({}, '', search_params ? '?' + search_params : window.location.pathname);

  toTop();
});

const login_test_step = ref('verify_email');
watch(login_test_step, async (_new) => {
  if (_new === 'success' || _new === 'register') {
    enableAside();

    const res = await fetch (props.save_url, {
      method: 'POST',
      body: JSON.stringify({
        'email': store.user.email,
        'purpose': props.registration.purpose,
        'package': props.registration.package,
      }),
      headers: {
        'content-type': 'application/json',
        'x-save-type': 'get-registration',
      },
    });
    const data = await res.json();
    if (data.success)  {
      // You can't reassign a reactive object directly so we need to copy the properties
      Object.assign(my_registration, data.registration);
    } else {
      msg(props.trans.toast_error_general);
    }
    my_registration.businessReferences.step_max = Math.max(my_registration.businessReferences.step_max, 1);
    ignoreUpdates(() => step.value = Math.max(1, my_registration.businessReferences.step_max));
  }
});

const sectorOptionsFirst = computed(() => {
  return props.sectorOptions.map(option => {
    const { children, ...rest } = option;
    return rest;
  });
});
const interestedRegionOptions = computed(() => {
  return props.regionOptions;
});

const sectorOptionsSecond = computed(() => {
  const p = my_registration.businessReferences.sector;
  if (p === undefined) {
    return [];
  }

  const ret = props.sectorOptions.find(option => {
    return option.key === p.toString();
  })?.children || [];

  // ret.map(option => {
  //   option.key = option.key.split('_')[1];
  // });

  return ret;
});

const disableConfidential = computed(() => {
  let c = 0;
  if (my_registration.businessReferences.turnoverLastFinancialYearConfidential) {
    c++;
  }
  if (my_registration.businessReferences.earningBeforeTaxesConfidential) {
    c++;
  }
  if (my_registration.businessReferences.askingPriceToBeAgreed) {
    c++;
  }

  return c > 1;
});
function setStep(v: number) {
  if (v <= my_registration.businessReferences.step_max) {
    step.value = v;
  }
  toTop();
}

const validation_run = ref(false);
async function save() {
  if (saving.value) {
    return;
  }
  saving.value = true;
  const res = await fetch(props.save_url, {
    method: 'POST',
    body: JSON.stringify({
      user: store.user,
      registration: my_registration,
    }),
    headers: {
      'content-type': 'application/json',
      'x-registration-token': my_registration.token || '',

      'x-save-type': 'save-listing',
      'x-save-step': step.value.toString(),
    },
  });
  if (!res.ok) {
    msg(props.trans.toast_error_general);
  }
  const data = await res.json();
  if (data.success) {
    Object.assign(my_registration, data.registration);
  } else {
    msg(props.trans.toast_error_general);
  }

  saving.value = false;
}
async function pay() {
  if (saving.value) {
    return;
  }
  validation_run.value = true;
  if (!validateStep(1)) {
    step.value = 1;
    return;
  }
  if (!validateStep(2)) {
    step.value = 2;
    return;
  }
  if (!validateStep(5)) {
    step.value = 5;
    return;
  }

  saving.value = true;
  const res = await fetch(props.save_url, {
    method: 'POST',
    body: JSON.stringify({
      user: store.user,
      registration: my_registration,
    }),
    headers: {
      'content-type': 'application/json',
      'x-registration-token': my_registration.token || '',
      'x-save-type': 'submit-listing',
    },
  });
  if (!res.ok) {
    alert('error');
  }
  const data = await res.json();
  if (data.success) {
    if (data.redirect) {
      window.location.href = data.redirect;
    }
  } else {
    alert('error');
  }

  saving.value = false;
}

const dropzoneOptions = computed(() => {
  return {
    url: props.image_url,
    thumbnailWidth: 150,
    maxFilesize: 1,
    acceptedFiles: 'image/*',
    maxFiles: MAX_FILES - (my_registration.personalData.files ? my_registration.personalData.files.length : 0),
    headers: {
      'x-registration-token': my_registration.token,
    },
    dictDefaultMessage: props.trans.step3.upload,
  }
});

function handleFileUploadSuccess({ response }: { response: any }) {
  if (response.success) {
    if (!my_registration.personalData.hasOwnProperty('files') || !my_registration.personalData.files) {
      my_registration.personalData.files = [];
    }
    my_registration.personalData.files.push(response.name);
  }
}
async function removeFile(f: string) {
  const res = await fetch(props.image_url + '?image=' + f, {
    method: 'DELETE',
    headers: {
      'x-registration-token': my_registration.token,
    },
  });
  if (res.ok) {
    my_registration.personalData.files = my_registration.personalData.files.filter((file: string) => file !== f);
  }
}

/* STEP 4 */
const summary_open = ref(true);

// ee.eePackage
function unsetEE(newVal: boolean) {
  if (!newVal && my_registration.extraOption.ee.eePackage) {
    my_registration.extraOption.ee.eePackage = false;
  }
  if (newVal) {
    let c = 0;
    my_registration.extraOption.ee.newsletter && c++;
    my_registration.extraOption.ee.moveUp && c++;
    my_registration.extraOption.ee.featuredCompany && c++;
    my_registration.extraOption.ee.homepage && c++;
    if (c > 3) {
      my_registration.extraOption.ee.eePackage = true;
    }
  }
}
watch(() => my_registration.extraOption.ee.newsletter, unsetEE);
watch(() => my_registration.extraOption.ee.moveUp, unsetEE);
watch(() => my_registration.extraOption.ee.featuredCompany, unsetEE);
watch(() => my_registration.extraOption.ee.homepage, unsetEE);
function setEE() {
  my_registration.extraOption.ee.newsletter = my_registration.extraOption.ee.eePackage;
  my_registration.extraOption.ee.moveUp = my_registration.extraOption.ee.eePackage;
  my_registration.extraOption.ee.featuredCompany = my_registration.extraOption.ee.eePackage;
  my_registration.extraOption.ee.homepage = my_registration.extraOption.ee.eePackage;
}

function unsetIp(newVal: boolean) {
  if (!newVal && my_registration.extraOption.ip.ipPackage) {
    my_registration.extraOption.ip.ipPackage = false;
  }
  if (newVal) {
    let c = 0;
    (store.webspace !== 'btknl') && my_registration.extraOption.ip.netherlands && c++;
    (store.webspace !== 'btkbe') && my_registration.extraOption.ip.belgium && c++;
    (store.webspace !== 'btkde') && my_registration.extraOption.ip.germany && c++;
    (store.webspace !== 'btkes') && my_registration.extraOption.ip.spain && c++;
    if (c > 2) {
      my_registration.extraOption.ip.ipPackage = true;
    }
  }
}
watch(() => my_registration.extraOption.ip.netherlands, unsetIp);
watch(() => my_registration.extraOption.ip.belgium, unsetIp);
watch(() => my_registration.extraOption.ip.germany, unsetIp);
watch(() => my_registration.extraOption.ip.spain, unsetIp);
function setIp() {
  (store.webspace !== 'btknl') && (my_registration.extraOption.ip.netherlands = my_registration.extraOption.ip.ipPackage);
  (store.webspace !== 'btkbe') && (my_registration.extraOption.ip.belgium = my_registration.extraOption.ip.ipPackage);
  (store.webspace !== 'btkde') && (my_registration.extraOption.ip.germany = my_registration.extraOption.ip.ipPackage);
  (store.webspace !== 'btkes') && (my_registration.extraOption.ip.spain = my_registration.extraOption.ip.ipPackage);
}

/* STEP 5 */
async function resetForm() {
  if (!confirm(props.trans.reset_form_confirm)) {
    return;
  }
  saving.value = true;
  const res = await fetch(props.save_url, {
    method: 'POST',
    body: JSON.stringify({
      purpose: my_registration.purpose,
    }),
    headers: {
      'content-type': 'application/json',
      'x-registration-token': my_registration.token,
      'x-save-type': 'reset-registration',
    },
  });

  if (!res.ok) {
    alert('error');
  }
  const data = await res.json();
  if (data.success) {
    Object.assign(my_registration, data.registration);
    ignoreUpdates(() => step.value = 1);
  } else {
    alert('error');
  }
  saving.value = false;
}
</script>

<template>
  <form class="form required-labels" style="position:relative;">
    <Toast position="top-right" />
    <template v-if="step === 0">
      <p v-html="trans.intro0"></p>

      <div class="info-box flow" style="margin-top:0;">
        <LoginTest v-model:step="login_test_step" :verify_email_url="verify_email_url" :login_url="login_url" :forgot_password_url="forgot_password_url" :trans="trans"></LoginTest>
      </div>

      <div class="listing-new-intro">
        <div class="flow" v-html="trans.intro1"></div>
        <div class="flow" v-html="trans.intro2"></div>
      </div>
    </template>

    <template v-else>
      <a ref="formTop"></a>
      <button type="button" style="width:38px;aspect-ratio: 1;position:absolute;right:0;top:-5em;" :title="trans.reset_form" v-if="my_registration.token" @click.prevent="resetForm" class="btn-solid btn-sm" :class="saving ? 'icon-spinner' : 'ico-xmark-regular'"></button>

      <ol class="listing-new-steps">
        <li :class="{enabled: my_registration.businessReferences.step_max > 0, active: step === 1}">
          <a href="#" @click.prevent="setStep(1)">
            <span class="step enabled">{{ trans.step }} 1</span>
            <span class="title">{{ trans.step1.tab }}</span>
          </a>
        </li>
        <li v-if="!is_package_light" :class="{enabled: my_registration.businessReferences.step_max > 1, active: step === 2}">
          <a href="#" @click.prevent="setStep(2)">
            <span class="step">{{ trans.step }} 2</span>
            <span class="title">{{ trans.step2.tab }}</span>
          </a>
        </li>

        <li v-if="is_sale && !is_package_light" :class="{enabled: my_registration.businessReferences.step_max > 2, active: step === 3}">
          <a href="#" @click.prevent="setStep(3)">
            <span class="step">{{ trans.step }} 3</span>
            <span class="title">{{ trans.step3.tab }}</span>
          </a>
        </li>
        <li v-if="!is_package_light" :class="{enabled: my_registration.businessReferences.step_max > 3, active: step === 4}">
          <a href="#" @click.prevent="setStep(4)">
            <span class="step">{{ trans.step }} {{ is_sale ? 4 : 3}}</span>
            <span class="title">{{ trans.step4.tab }}</span>
          </a>
        </li>
        <li :class="{enabled: my_registration.businessReferences.step_max > 4, active: step === 5}">
          <a href="#" @click.prevent="setStep(5)">
            <span class="step">{{ trans.step }} {{ is_package_light ? 2 : (is_sale ? 5 : 4) }}</span>
            <span class="title">{{ trans.step5.tab }}</span>
          </a>
        </li>
        <li :class="{enabled: my_registration.businessReferences.step_max > 5, active: step === 6}">
          <a href="#" @click.prevent="setStep(6)">
            <span class="step">{{ trans.step }} {{ is_package_light ? 3 : (is_sale ? 6 : 5) }}</span>
            <span class="title">{{ trans.step6.tab }}</span>
          </a>
        </li>
      </ol>

      <template v-if="step === 1">
        <template v-if="is_sale && !is_package_light && (store.webspace === 'btknl')">
          <h2>{{ trans.step1.choice }}</h2>
          <p>{{ trans.step1.choice_info }}</p>

          <ul class="listing-package flow">
            <li>
              <label class="ui-radio"><input type="radio" :value="false" v-model="my_registration.businessReferences.premium" name="package"> <span>{{ trans.step1.premium_regular }}</span></label>
              <p>{{ trans.step1.premium_regular_info }}</p>
            </li>
            <li>
              <label class="ui-radio"><input type="radio" :value="true" v-model="my_registration.businessReferences.premium" name="package"> <span>{{ trans.step1.premium_premium }}</span></label>
              <p>{{ trans.step1.premium_premium_info }}</p>
            </li>
          </ul>
          <hr>
        </template>

        <h2>{{ trans.step1.props }}</h2>
        <p>{{ trans.step1.props_info }}</p>

        <div class="row">
          <div>
            <label for="registration-employees">{{ trans.step1.employees }}</label> <tip :content="trans.step1.employees_tip" />
            <Select v-if="is_sale" id="registration-employees"  v-model="my_registration.businessReferences.employeesInFTE"
                    :options="employeeOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': isInvalidSelect('employeesInFTE')}"
            />
            <MultiSelect v-else id="registration-employees" v-model="my_registration.businessReferences.employeesInFTE" display="chip"
                         :options="employeeOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('employeesInFTE')}"
            />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="registration-location">{{ trans.step1.location }}</label> <tip :content="trans.step1.location_tip" />
            <TreeSelect id="registration-location" v-model="my_registration.businessReferences.location"
                        :selectionMode="is_sale ? 'single' : 'checkbox'" :display="is_sale ? 'comma' : 'chip'"
                        :options="regionOptions" optionValue="key" :placeholder="trans.make_choice" required
                        :filter="true"
                        :expandedKeys="expandedLocations"
                        :class="{'p-invalid': isInvalidSelect('location')}"
            />

            <label v-if="!is_sale" class="ui-checkbox mt-xs not-required">
              <input type="checkbox" v-model="my_registration.businessReferences.locationAnonymous" />
              <span>{{ trans.step1.location_anonymous }}</span>
            </label>
          </div>
        </div>

        <div class="row" v-if="is_sale">
          <div>
            <label for="registration-interested-location">{{ trans.step1.interested_location }}</label> <tip :content="trans.step1.interested_location_tip" />
            <TreeSelect id="registration-interested-location" v-model="my_registration.businessReferences.interestedLocation"
                        selectionMode="checkbox" display="chip"
                        :options="interestedRegionOptions" optionValue="key" :placeholder="trans.make_choice"
                        :filter="true"
                        :expandedKeys="expandedLocations"
            />
          </div>
        </div>

        <template v-if="is_sale">
          <div class="row">
            <div>
              <label for="registration-location">{{ trans.step1.branche }}</label>
              <Select id="registration-location" v-model="my_registration.businessReferences.sector" :options="sectorOptionsFirst"
                      optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                      @change="my_registration.businessReferences.typeOfCompany = ''"
                      :class="{'p-invalid': isInvalidSelect('sector')}"
              />
            </div>
          </div>

          <div class="row">
            <div>
              <label for="registration-type_of_company">{{ trans.step1.type_of_company }}</label> <tip :content="trans.step1.type_of_company_tip" />
              <Select id="registration-type_of_company" v-model="my_registration.businessReferences.typeOfCompany" :options="sectorOptionsSecond"
                      optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                      :class="{'p-invalid': isInvalidSelect('typeOfCompany')}"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="row">
            <div>
              <label for="registration-type_of_company">{{ trans.step1.type_of_company }}</label> <tip :content="trans.step1.type_of_company_tip" />
              <TreeSelect id="registration-type_of_company" display="chip" v-model="my_registration.businessReferences.typeOfCompany"
                          :options="sectorOptions" selectionMode="checkbox" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                          :class="{'p-invalid': isInvalidSelect('typeOfCompany')}"
              />
            </div>
          </div>
        </template>

        <div class="row">
          <div>
            <label for="registration-legal">{{ trans.step1.legal }}</label> <tip :content="trans.step1.legal_tip" />
            <Select v-if="is_sale" id="registration-legal"  v-model="my_registration.businessReferences.legalEntity"
                    :options="legalOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': isInvalidSelect('legalEntity')}"
            />
            <MultiSelect v-else id="registration-legal" v-model="my_registration.businessReferences.legalEntity" display="chip"
                         :options="legalOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('legalEntity')}"
            />
          </div>

          <div>
            <label for="registration-transaction">{{ trans.step1.transaction }}</label> <tip :content="trans.step1.transaction_tip" />
            <Select v-if="is_sale" id="registration-transaction"  v-model="my_registration.businessReferences.typeOfTransaction"
                    :options="transactionOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': isInvalidSelect('typeOfTransaction')}"
                    :disabled="(store.webspace === 'btknl') && (my_registration.businessReferences.legalEntity === 1 || my_registration.businessReferences.legalEntity === 2)"
            />
            <MultiSelect v-else id="registration-transaction" v-model="my_registration.businessReferences.typeOfTransaction" display="chip"
                         :options="transactionOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('typeOfTransaction')}"
            />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="registration-acquisition">{{ trans.step1.acquisition }}</label> <tip :content="trans.step1.acquisition_tip" />
            <MultiSelect id="registration-acquisition" v-model="my_registration.businessReferences.typeOfAcquisition" display="chip"
                         :options="acquisitionOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('typeOfAcquisition')}"
            />
          </div>

          <div>
            <label for="registration-phase">{{ trans.step1.phase }}</label> <tip :content="trans.step1.phase_tip" />
            <Select v-if="is_sale" id="registration-phase"  v-model="my_registration.businessReferences.lifePhaseEnterprise"
                    :options="phaseOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': isInvalidSelect('lifePhaseEnterprise')}"
            />
            <MultiSelect v-else id="registration-phase" v-model="my_registration.businessReferences.lifePhaseEnterprise" display="chip"
                         :options="phaseOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('lifePhaseEnterprise')}"
            />
          </div>
        </div>

        <hr>

        <h2>{{ trans.step1.financial_heading }}</h2>

        <div class="row">
          <div>
            <label for="registration-turnover">{{ trans.step1.turnover }}</label> <tip :content="trans.step1.turnover_tip" />
            <Select v-if="is_sale" id="registration-turnover"  v-model="my_registration.businessReferences.turnoverLastFinancialYear"
                    :options="turnoverOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': isInvalidSelect('turnoverLastFinancialYear')}"
            />
            <MultiSelect v-else id="registration-turnover" v-model="my_registration.businessReferences.turnoverLastFinancialYear" display="chip"
                         :options="turnoverOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('turnoverLastFinancialYear')}"
            />
            <label v-if="is_sale && !is_package_light" class="ui-checkbox mt-xs not-required">
              <input type="checkbox" v-model="my_registration.businessReferences.turnoverLastFinancialYearConfidential"
                     :disabled="disableConfidential && !my_registration.businessReferences.turnoverLastFinancialYearConfidential"
              />
              <span>{{ trans.step1.confidential }}</span>
            </label>
          </div>
        </div>

        <div class="row">
          <div>
            <label for="registration-earnings">{{ trans.step1.earnings }}</label> <tip :content="trans.step1.earnings_tip" />
            <Select v-if="is_sale" id="registration-earnings"  v-model="my_registration.businessReferences.earningBeforeTaxes"
                    :options="earningsOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': isInvalidSelect('earningBeforeTaxes')}"
            />
            <MultiSelect v-else id="registration-earnings" v-model="my_registration.businessReferences.earningBeforeTaxes" display="chip"
                         :options="earningsOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('earningBeforeTaxes')}"
            />
            <label v-if="is_sale && !is_package_light" class="ui-checkbox mt-xs not-required">
              <input type="checkbox" v-model="my_registration.businessReferences.earningBeforeTaxesConfidential"
                     :disabled="disableConfidential && !my_registration.businessReferences.earningBeforeTaxesConfidential"
              />
              <span>{{ trans.step1.confidential }}</span>
            </label>
          </div>
        </div>

        <div class="row">
          <div>
            <label for="registration-price">{{ trans.step1.price }}</label> <tip :content="trans.step1.price_tip" />
            <MultiSelect id="registration-price" v-model="my_registration.businessReferences.askingPrice" display="chip"
                         :options="priceOptions" optionLabel="label" optionValue="key" :placeholder="trans.make_choice" required
                         :class="{'p-invalid': isInvalidSelect('askingPrice')}"
            />
            <label v-if="is_sale && !is_package_light" class="ui-checkbox mt-xs not-required">
              <input type="checkbox" v-model="my_registration.businessReferences.askingPriceToBeAgreed"
                     :disabled="disableConfidential && !my_registration.businessReferences.askingPriceToBeAgreed"
              />
              <span>{{ trans.step1.to_agree }}</span></label>
          </div>
        </div>

        <div v-if="is_sale && disableConfidential" class="notice notice-warning text-center">
          Let op: Er kunnen slechts twee financiële kenmerken als niet zichtbaar gemarkeerd worden
        </div>
      </template>

      <template v-else-if="step === 2">
        <div class="flow">
          <h2>{{ trans.step2.description }}</h2>
          <p>{{ trans.step2.description_info }}</p>

          <!-- #5608 Submit form - Remove translation option -->
<!--          <hr>-->

<!--          <h3>{{ trans.step2.language }}</h3>-->
<!--          <p>{{ trans.step2.language_info }}</p>-->

<!--          <ol class="tabs">-->
<!--            <li><button aria-label="Enter Dutch teksts" @click.prevent="active_lang = 'nl'" class="btn-solid btn-sm" :class="{'btn-gray': active_lang !== 'nl'}" href="#">Dutch</button></li>-->
<!--            <li><button aria-label="Enter English teksts" @click.prevent="active_lang = 'en'" class="btn-solid btn-sm" :class="{'btn-gray': active_lang !== 'en'}" href="#">English</button></li>-->
<!--          </ol>-->

          <hr>
        </div>

        <div class="row">
          <div>
            <label for="listing-title">{{ trans.step2.title }}</label>
            <input id="listing-title" type="text" v-model="my_registration.description[active_lang].name" placeholder="" required
                   :class="{'p-invalid': validation_run && !my_registration.description[store.locale].name}"
            />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="listing-history">{{ trans.step2.history }}</label> <tip :content="trans.step2.history_tip" />
            <editor id="listing-history" v-model="my_registration.description[active_lang].history" />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="listing-history">{{  trans.step2.activities }}</label> <tip :content="trans.step2.activities_tip" />
            <editor id="listing-history" v-model="my_registration.description[active_lang].activities" />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="listing-history">{{  trans.step2.unique }}</label> <tip :content="trans.step2.unique_tip" />
            <editor id="listing-history" v-model="my_registration.description[active_lang].uniqueSellingPoints" />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="listing-history">{{  trans.step2.other }}</label> <tip :content="trans.step2.other_tip" />
            <editor id="listing-history" v-model="my_registration.description[active_lang].other" />
          </div>
        </div>
      </template>

      <template v-else-if="step === 3">
        <h2>{{ trans.step3.description }}</h2>
        <p>{{ trans.step3.description_info }}</p>

        <vueDropzone
            v-if="my_registration.personalData.files?.length < MAX_FILES"
            id="dropzone"
            :options="dropzoneOptions"
            @success="handleFileUploadSuccess"
        />
        <div v-if="my_registration.personalData.files.length > 0">
          <h3>{{ trans.step3.uploaded }}</h3>
          <div id="new-gallery" class="gallery">
            <div v-for="file in my_registration.personalData.files" :key="file">
              <button aria-label="Remove image" @click.prevent="removeFile(file)" class="btn-solid btn-sm">&times;</button>
              <picture>
                <img :src="image_url + '?token=' + my_registration.token + '&image=' + file" alt="file" width="" />
              </picture>
            </div>
          </div>
          <svg width="0" height="0">
            <filter id="svg-blur-filter" x="-15%" y="-15%" width="130%" height="130%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="9" result="blurred"></feGaussianBlur>
              <feColorMatrix type="saturate" in="blurred" values="5"></feColorMatrix>
              <feComponentTransfer in="blurred">
                <feFuncA type="table" tableValues="0 0.99"></feFuncA>
              </feComponentTransfer>
              <feComposite in="SourceGraphic" operator="over" in2=""></feComposite>
            </filter>
          </svg>
        </div>

      </template>

      <template v-else-if="step === 4">
        <h2>{{ trans.step4.description }}</h2>
        <p>{{ trans.step4.description_info }}</p>

        <h3>{{ trans.step4.options_a }}</h3>
        <ul class="extra-options">
          <li class="extra-option-newsletter" v-if="false !== props.prices.options.newsletter">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.newsletter" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.newsletter_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.newsletter }}
                <span>&euro; {{ prices.options.newsletter }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-moveup" v-if="false !== props.prices.options.moveup">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.moveUp" />
                    <span></span>
                  </span>

                  <span class="chip">{{ trans.popular }}</span>

                  <tip :content="trans.step4.moveup_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.moveup }}
                <span>&euro; {{ prices.options.moveup }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-featured" v-if="false !== props.prices.options.featured">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.featuredCompany" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.featured_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.featured }}
                <span>&euro; {{ prices.options.featured }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-homepage" v-if="false !== props.prices.options.homepage">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.homepage" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.homepage_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.homepage }}
                <span>&euro; {{ prices.options.homepage }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-eepackage" v-if="false !== props.prices.options.eepackage">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.eePackage" @change="setEE" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.eepackage_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.eepackage }}
                <span>&euro; {{ prices.options.eepackage }}</span>
              </div>
            </label>
          </li>
        </ul>

        <h3>{{ trans.step4.options_b }}</h3>
        <ul class="extra-options">
          <li class="extra-option-facebook" v-if="false !== props.prices.options.facebook">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.facebook" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.facebook_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.facebook }}
                <span>&euro; {{ prices.options.facebook }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-linkedin" v-if="false !== props.prices.options.linkedin">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ee.linkedin" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.linkedin_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.linkedin }}
                <span>&euro; {{ prices.options.linkedin }}</span>
              </div>
            </label>
          </li>
        </ul>

        <template v-if="is_sale">
          <h3>{{ trans.step4.options_c }}</h3>
          <ul class="extra-options">
            <li class="extra-option-response" v-if="false !== props.prices.options.response">
              <label>
                <div class="top">
                  <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.as.responseManager"/>
                    <span></span>
                  </span>

                    <tip :content="trans.step4.response_tip"/>
                  </div>
                </div>
                <div class="bottom">
                  {{ trans.step4.response }}
                  <span>&euro; {{ prices.options.response }}</span>
                </div>
              </label>
            </li>

            <li class="extra-option-mailing" v-if="false !== props.prices.options.mailing">
              <label>
                <div class="top">
                  <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.as.directMailing"/>
                    <span></span>
                  </span>

                    <tip :content="trans.step4.mailing_tip"/>
                  </div>
                </div>
                <div class="bottom">
                  {{ trans.step4.mailing }}
                  <span>&euro; {{ prices.options.mailing }}</span>
                </div>
              </label>
            </li>
            <li class="extra-option-shortlist" v-if="false !== props.prices.options.shortlist">
              <label>
                <div class="top">
                  <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.as.mailingShortList"/>
                    <span></span>
                  </span>

                    <tip :content="trans.step4.shortlist_tip"/>
                  </div>
                </div>
                <div class="bottom">
                  {{ trans.step4.shortlist }}
                  <span>&euro; {{ prices.options.shortlist }}</span>
                </div>
              </label>
            </li>
          </ul>
        </template>


        <h3>{{ trans.step4.options_d }}</h3>
        <ul class="extra-options">
          <li class="extra-option-netherlands" v-if="(false !== props.prices.options.netherlands) && store.webspace !== 'btknl'">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ip.netherlands" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.netherlands_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.netherlands }}
                <span>&euro; {{ prices.options.netherlands }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-belgium" v-if="(false !== props.prices.options.belgium) && store.webspace !== 'btkbe'">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ip.belgium" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.belgium_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.belgium }}
                <span>&euro; {{ prices.options.belgium }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-germany" v-if="(false !== props.prices.options.germany) && store.webspace !== 'btkde'">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ip.germany" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.germany_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.germany }}
                <span>&euro; {{ prices.options.germany }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-spanish" v-if="(false !== props.prices.options.spain) && store.webspace !== 'btkes'">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ip.spain" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.spain_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.spain }}
                <span>&euro; {{ prices.options.spain }}</span>
              </div>
            </label>
          </li>
          <li class="extra-option-ipPackage" v-if="false !== props.prices.options.package">
            <label>
              <div class="top">
                <div>
                  <span class="ui-checkbox">
                    <input type="checkbox" v-model="my_registration.extraOption.ip.ipPackage" @change="setIp" />
                    <span></span>
                  </span>

                  <tip :content="trans.step4.package_tip" />
                </div>
              </div>
              <div class="bottom">
                {{ trans.step4.package }}
                <span>&euro; {{ prices.options.package }}</span>
              </div>
            </label>
          </li>

        </ul>

        <div class="summary">
          <button @click.prevent="summary_open = !summary_open" class="toggle">
            <span>{{ trans.summary }}</span>
            <span :class="{'ico-chevron-down-solid': summary_open, 'ico-chevron-up-solid': !summary_open}"></span>
          </button>
          <div v-if="summary_open" class="content">
            <Prices :package="prices.package" :trans="trans" :my_registration="my_registration"
                    :prices="prices" :toast="msg" :save-url="save_url" :save-voucher="save" />
          </div>
        </div>
      </template>

      <template v-else-if="step === 5">
        <h2>{{ trans.step5.description }}</h2>
        <p>{{ trans.step5.description_info }}</p>

        <div class="row">
          <div>
            <label for="reaction-form-company">{{ trans.account.company }}</label>
            <input v-model="my_registration.personalData.name" :class="{'p-invalid': validation_run && is_sale && !my_registration.personalData.name}"
                   id="reaction-form-company" type="text" placeholder="" :required="is_sale" />
          </div>
        </div>

        <template v-if="store.webspace === 'btknl' || store.webspace === 'btkde'">
          <div class="row">
            <div>
              <label for="reaction-form-coc">{{ trans.account.coc_number }}</label>
              <input v-model="my_registration.businessReferences.chamberOfCommerce"
                     id="reaction-form-coc" type="text" placeholder="" />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="row">
            <div>
              <label for="reaction-form-vat2">{{ trans.step5.vat_number }}</label>
              <input v-model="my_registration.personalData.vatNumber2" required
                     id="reaction-form-vat2" type="text" placeholder="" />
            </div>
          </div>
        </template>

        <div class="row">
          <div>
            <label for="reaction-form-form-of-address">{{ trans.account.form_of_address }}</label>
            <div class="inputs-group inline">
              <label v-for="fa in trans.select_options.formOfAddress" class="ui-radio" required :key="fa.id">
                <input v-model="my_registration.personalData.formOfAddress" id="reaction-form-form-of-address" name="formOfAddress" type="radio" :value="fa.id"
                       :class="{'p-invalid': validation_run && !my_registration.personalData.formOfAddress}"
                />
                <span>{{ fa.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="size-md">
            <label for="reaction-form-first-name">{{ trans.account.first_name }}</label>
            <input v-model="my_registration.personalData.firstName" :class="{'p-invalid': validation_run && !my_registration.personalData.firstName}"
                   id="reaction-form-first-name" type="text" placeholder="" required />
          </div>
          <div>
            <label for="reaction-form-last-name">{{ trans.account.last_name }}</label>
            <input v-model="my_registration.personalData.lastName" :class="{'p-invalid': validation_run && !my_registration.personalData.lastName}"
                   id="reaction-form-last-name" type="text" placeholder="" required />
          </div>
        </div>

        <Address :trans="trans.address"
                 v-model:postcode="my_registration.personalData.postcode"
                 v-model:number="my_registration.personalData.number"
                 v-model:street="my_registration.personalData.street"
                 v-model:town="my_registration.personalData.town" />

        <div class="row">
          <div>
            <label for="reaction-form-phone">{{ trans.account.phone }}</label>
            <input v-model="my_registration.personalData.phone" :class="{'p-invalid': validation_run && !my_registration.personalData.phone}" id="reaction-form-phone" type="tel" required />
          </div>

          <div>
            <label for="reaction-form-phone-mobile">{{ trans.account.phone_mobile }}</label>
            <input v-model="my_registration.personalData.phoneMobile" :class="{'p-invalid': validation_run && !my_registration.personalData.phoneMobile}" id="reaction-form-phone-mobile" type="tel" required />
          </div>
        </div>

        <div class="row">
          <div>
            <label for="reaction-form-contact-time">{{ trans.account.contact_time }}</label>
            <Select id="reaction-form-contact-time"  v-model="my_registration.personalData.presetContactTimeId"
                    :options="trans.select_options.contactTimes" optionLabel="label" optionValue="id" :placeholder="trans.make_choice" required
                    :class="{'p-invalid': validation_run && !my_registration.personalData.presetContactTimeId}"
            />
          </div>
        </div>

        <label class="ui-checkbox">
          <input type="checkbox" v-model="my_registration.personalData.foreignCompany" />
          <span>{{ trans.step5.foreign_company }}</span>
        </label>

        <label style="margin-left: 35px;margin-top: -1em;" class="ui-checkbox" v-if="my_registration.personalData.foreignCompany">
          <input type="checkbox" v-model="my_registration.personalData.isVatNull" />
          <span>{{ trans.vat }} 0%</span>
        </label>

        <template v-if="my_registration.personalData.foreignCompany && my_registration.personalData.isVatNull">
          <template v-if="store.webspace === 'btkde'">
            <div style="padding-left: 35px;margin-top: -1em;">
              <label for="account-tax-id">{{ trans.step5.tax_id }}</label>
              <input v-model="my_registration.personalData.taxId"
                     id="account-tax-id" type="text"
                     placeholder="" required />
            </div>
            <div style="padding-left: 35px;margin-top: -1em;">
              <label for="account-tax-number">{{ trans.step5.tax_number }}</label>
              <input v-model="my_registration.personalData.taxNumber"
                     id="account-tax-number" type="text"
                     placeholder="" required />
            </div>
          </template>
          <template v-else>
            <div style="padding-left: 35px;margin-top: -1em;">
              <label for="account-vat-number">{{ trans.step5.vat_number }}</label>
                <input v-model="my_registration.personalData.vatNumber"
                     id="account-vat-number" type="text"
                     placeholder="" required />
            </div>
          </template>
        </template>

        <label class="ui-checkbox">
          <input type="checkbox" v-model="my_registration.personalData.subscriber"
          />
          <span>{{ trans.step5.register_newsletter }}</span>
        </label>
        <label class="ui-checkbox" :class="{'p-invalid': validation_run && !my_registration.personalData.terms}">
          <input type="checkbox" required v-model="my_registration.personalData.terms" />
          <span v-html="trans.step5.agree_terms"></span>
        </label>

        <template v-if="!store.user.id">
          <hr>

          <h2>{{ trans.step5.create_password }}</h2>

          <div class="row">
            <div>
              <label for="reaction-form-new-password">{{ trans.step5.password }}</label>
              <input v-model="my_registration.personalData.password" :class="{'p-invalid': validation_run && (my_registration.personalData.password.length < 6)}"
                     id="reaction-form-new-password" type="password" minlength="6"
                     autocomplete="new-password" placeholder="" required>
            </div>

            <div>
              <label for="reaction-form-verify-password">{{ trans.step5.password_repeat }}</label>
              <input v-model="my_registration.personalData.password_repeat" :class="{'p-invalid': validation_run && ((my_registration.personalData.password_repeat < 6) || (my_registration.personalData.password !== my_registration.personalData.password_repeat))}"
                     id="reaction-form-verify-password" type="password" minlength="6"
                     autocomplete="new-password" placeholder="" required>
            </div>
          </div>
        </template>
      </template>

      <template v-else-if="step === 6">
        <h2>{{ trans.step6.description }}</h2>
        <p>{{ trans.step6.description_info }}</p>

        <table class="checkout-table step6">
          <tbody>
          <tr>
            <td class="label">{{ trans.account.first_name }}</td>
            <td class="value">{{ my_registration.personalData.firstName }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.account.last_name }}</td>
            <td class="value">{{ my_registration.personalData.lastName }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.address.postcode }}</td>
            <td class="value">{{ my_registration.personalData.postcode }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.address.house_number }}</td>
            <td class="value">{{ my_registration.personalData.number }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.address.street }}</td>
            <td class="value">{{ my_registration.personalData.street }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.address.town }}</td>
            <td class="value">{{ my_registration.personalData.town }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.account.phone }}</td>
            <td class="value">{{ my_registration.personalData.phone }}</td>
          </tr>
          <tr>
            <td class="label">{{ trans.account.phone_mobile }}</td>
            <td class="value">{{ my_registration.personalData.phoneMobile }}</td>
          </tr>
          </tbody>
        </table>

        <hr style="margin-block: 0;">

        <Prices :header="true" :package="prices.package" :trans="trans" :my_registration="my_registration"
                :prices="prices" :toast="msg" :save-url="save_url" :save-voucher="save" extra-class="step6" />
      </template>

      <div class="form-controls">
        <a v-if="step > 1" href="#" class="prev icon-rarr icon-180 icon-before" @click.prevent="prevStep">{{ trans.back }}</a>
        <span v-else></span>

        <button v-if="step < 6" class="btn-solid" :class="saving ? 'icon-spinner' : 'icon-rarr'" @click.prevent="nextStep" :disabled="saving">{{ trans.next_step }}</button>
        <button v-else class="btn-solid" :class="saving ? 'icon-spinner' : 'icon-rarr'" @click.prevent="pay" :disabled="saving">{{ trans.pay }}</button>
      </div>
    </template>
  </form>
</template>
